import { Container } from './primitives/Misc.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';
import { useAnim3d } from '../hooks/useAnim3d.js';
import './Duplicates.css';

export interface DuplicatesProps {
  moneyBig: string;
  ccyCode: CurrencyCode;
}

interface OrbitCard {
  source: string;
  name: string;
  detail: string;
  rotateY: number;
  translateZ: number;
  translateY: number;
  tint?: boolean;
}

const ORBIT: OrbitCard[] = [
  { source: 'crm · contact', name: 'Aisha Rahman', detail: 'a.rahman@northgate.co.uk', rotateY: -62, translateZ: 196, translateY: -72 },
  { source: 'accounting · customer', name: 'Northgate Ltd', detail: '2 open invoices', rotateY: -34, translateZ: 210, translateY: 46 },
  { source: 'email platform · subscriber', name: 'A. Rahman', detail: 'bounced 3×', rotateY: -10, translateZ: 178, translateY: -34 },
  { source: 'spreadsheet · 2024 event list', name: 'AISHA RAHMAN', detail: 'no consent recorded', rotateY: 16, translateZ: 206, translateY: 66 },
  { source: 'helpdesk · requester', name: 'rahman, aisha', detail: '+44 7700 900182', rotateY: 40, translateZ: 192, translateY: -64 },
  { source: 'score 0.97 · auto-merge', name: 'phone exact + domain', detail: 'held for your approval', rotateY: 64, translateZ: 214, translateY: 22, tint: true },
];

/** The rotating cluster-of-records visual. */
function ClusterOrbit() {
  return (
    <div className="land-dupes__orbit-frame">
      <div className="land-dupes__orbit" data-anim3d="1">
        <div className="land-dupes__survivor">
          <div>
            <div className="land-dupes__survivor-label">survivor</div>
            <div className="land-dupes__survivor-name">
              Aisha
              <br />
              Rahman
            </div>
          </div>
        </div>
        {ORBIT.map((card) => (
          <div
            key={card.source}
            className={`land-dupes__card${card.tint ? ' land-dupes__card--tint' : ''}`}
            style={{
              transform: `rotateY(${card.rotateY}deg) translateZ(${card.translateZ}px) translateY(${card.translateY}px)`,
            }}
          >
            <div className="land-dupes__card-source">{card.source}</div>
            <div className="land-dupes__card-name">{card.name}</div>
            <div className="land-dupes__card-detail">{card.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Step 03 of the hero's pipeline, in detail. This was the hero until 2026-07-30;
 * it moved here so the page leads with what Dedupr is for rather than with the
 * hardest single step inside it. The copy is unchanged — it was never the
 * problem, its position was.
 */
export function Duplicates({ moneyBig, ccyCode }: DuplicatesProps) {
  useAnim3d();

  return (
    <Container id="duplicates" className="land-section">
      <div className="land-mono-label">step 03 · deduplicate</div>
      <h2 className="land-dupes__title">
        Nine records. One customer. Somebody has to decide which version survives.
      </h2>
      <div className="land-dupes__split">
        <div>
          <p className="land-dupes__lede">
            Your customer sits in your CRM, your email platform, your accounting system and three
            spreadsheets — spelled differently in each. Dedupr reads every one of those systems,
            builds the cluster, shows the evidence behind every link, and proposes the surviving
            record field by field. You approve. We snapshot, write, and keep it reversible for thirty
            days.
          </p>
          <div className="land-dupes__stat">
            <div className="land-dupes__stat-value">{moneyBig}</div>
            <div className="land-dupes__stat-label">
              illustrative example, {ccyCode} — your own report shows the formula and your own figures
            </div>
          </div>
        </div>
        <div>
          <ClusterOrbit />
          <div className="land-dupes__caption">showing 6 of 9 records in this cluster</div>
        </div>
      </div>
    </Container>
  );
}
